import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import type { Id } from '../../api/types';
import { Toast } from '../../components';
import { useQltClassNoteStore } from '../../stores/qlt';
import { ClassLogbookEditor } from './ClassLogbookEditor';
import { classMarkLabel, hydrateCommentText } from './classNoteFields';
import '../../styles/class-management.css';

export interface ClassLogbookCardProps {
  periodLabel: string;
  classTitle?: string;
  subjectTitle?: string;
  lessonTitle?: string;
  classNoteId?: Id;
}

function linkedLessonName(value: unknown): string {
  if (!value || typeof value !== 'object') return '';
  return String((value as { name?: string }).name || '');
}


export function ClassLogbookCard({ periodLabel, classTitle, subjectTitle, lessonTitle, classNoteId }: ClassLogbookCardProps) {
  const { t } = useTranslation(['panels', 'common']);
  const note = useQltClassNoteStore((state) => state.currentNote);
  const linkedLesson = useQltClassNoteStore((state) => state.currentLesson);
  const [editorOpen, setEditorOpen] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  // the upsert blanks note.lesson on a fresh record, so the board's own lesson title is the backstop
  const lessonName = linkedLessonName(linkedLesson?.topicLesson) || note?.lesson || lessonTitle || '';
  const mark = classMarkLabel(note?.data);
  // the same text the editor box shows; a legacy "a; b" record reads as a sentence there too
  const comment = hydrateCommentText(note?.data).text;

  return (
    <>
      <section className="cm-card">
        <div className="cm-card__head"><h3 className="cm-card__title">{t('logbook.lessonTitle')}</h3></div>
        <p className="cm-card__lead">{lessonName || t('empty.none')}</p>
      </section>
      <section className="cm-card">
        <div className="cm-card__head">
          <h3 className="cm-card__title">{t('logbook.title')}</h3>
          <button type="button" className="cm-card__more" disabled={!classNoteId || !note} onClick={() => setEditorOpen(true)}>{t('common:actions.add')}</button>
        </div>
        {!classNoteId ? <p className="cm-card__empty">{t('logbook.notRecorded')}</p> : (
          <dl className="cm-card__rows">
            <div className="cm-card__row"><dt>{t('logbook.mark')}</dt><dd>{mark || t('empty.none')}</dd></div>
            <div className="cm-card__row"><dt>{t('logbook.commentRow')}</dt><dd><span className="cm-card__line cm-card__text">{comment || t('empty.none')}</span></dd></div>
          </dl>
        )}
      </section>
      <ClassLogbookEditor open={editorOpen} periodLabel={periodLabel} classTitle={classTitle} subjectTitle={subjectTitle} onClose={() => setEditorOpen(false)} onSaved={() => setSavedToast(true)} />
      <Toast open={savedToast} message={t('logbook.saved')} onClose={() => setSavedToast(false)} />
    </>
  );
}
